import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
  Alert,
  useTheme,
  IconButton,
  Button,
} from "@mui/material";
import {
  WarningAmber as WarningIcon,
  DeleteForever as DeleteIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

const DeleteTemplateDialog = ({
  deleteTarget,
  setDeleteTarget,
  deleting,
  confirmDelete,
}) => {
  const theme = useTheme();
  const open = Boolean(deleteTarget);

  return (
    <Dialog
      open={open}
      onClose={() => !deleting && setDeleteTarget(null)}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: 400,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          overflow: "visible",
        },
      }}
    >
      {/* 1. 头部：警示风格 */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: "error.lighter", // 浅红色背景
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTopLeftRadius: "inherit",
          borderTopRightRadius: "inherit",
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              p: 1,
              bgcolor: "error.main",
              borderRadius: "50%",
              display: "flex",
              color: "white",
            }}
          >
            <WarningIcon fontSize='small' />
          </Box>
          <Typography variant='h6' fontWeight={700} color='error.main'>
            Delete Template
          </Typography>
        </Stack>
        <IconButton
          onClick={() => setDeleteTarget(null)}
          disabled={deleting}
          sx={{ color: "error.main" }}
          size='small'
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        <Stack spacing={2}>
          <Typography variant='body1' color='text.primary'>
            Are you sure you want to permanently delete this template?
          </Typography>

          {/* 2. 高亮目标信息 */}
          <Box
            sx={{
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <InfoIcon color='action' />
            <Typography
              variant='body2'
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            >
              {deleteTarget?.template_code}{" "}
              <span style={{ color: theme.palette.text.secondary }}>
                v{deleteTarget?.version}
              </span>
            </Typography>
          </Box>

          {/* 3. 约束提示 */}
          <Alert
            severity='warning'
            icon={<InfoIcon />}
            sx={{ fontSize: "0.875rem" }}
          >
            Templates referenced by active schedules cannot be deleted.
          </Alert>
        </Stack>
      </DialogContent>

      {/* 4. 底部操作 */}
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            fullWidth
            variant='outlined'
            sx={{ borderRadius: 2, py: 1.2 }}
          >
            Cancel
          </Button>
          <Button
            color='error'
            variant='contained'
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={deleting ? null : <DeleteIcon />}
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              bgcolor: "error.main",
              "&:hover": { bgcolor: "error.dark" },
            }}
          >
            {deleting ? "Deleting..." : "Yes, Delete"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteTemplateDialog;
